import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaToggleOn, FaToggleOff, FaArrowLeft } from 'react-icons/fa'; // Добавлена FaArrowLeft
import './MarketingManagement.css';

const MarketingManagement = () => {
  const [rules, setRules] = useState([]);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);

  const [name, setName] = useState('');
  // const [description, setDescription] = useState(''); // Удалено
  const [conditions, setConditions] = useState([{ requiredPostId: '' }]);
  const [targetPostId, setTargetPostId] = useState('');
  const [newPrice, setNewPrice] = useState('');

  const getAuthHeaders = () => {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'X-Telegram-ID': localStorage.getItem('telegram_id')
    };
  };

  // Define fetchData at the component level, memoized with useCallback
  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
        console.log('Fetching data (rules and content)...');
        const [rulesResponse, contentResponse] = await Promise.all([
            fetch('/api/marketing', { headers: getAuthHeaders() }),
            fetch('/api/content?limit=1000&published=all', { headers: getAuthHeaders() })
        ]);

        if (!rulesResponse.ok) throw new Error('Не удалось загрузить правила');
        const rulesData = await rulesResponse.json();
        console.log('Raw rulesData:', rulesData);
        setRules(rulesData);

        if (!contentResponse.ok) throw new Error('Не удалось загрузить контент');
        const contentData = await contentResponse.json();
        console.log('Raw contentData:', contentData);
        setContents(contentData.content || []);

    } catch (err) {
        console.error('Error in fetchData:', err);
        setError(err.message);
    } finally {
        setLoading(false);
    }
  }, [setLoading, setError, setRules, setContents]); // getAuthHeaders is stable

  // Initial data fetch on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Re-fetch data when the form is shown to ensure content list is up-to-date
  useEffect(() => {
    if (showForm) {
      console.log('Form is now visible, re-fetching data to ensure content list is up-to-date.');
      fetchData();
    }
  }, [showForm, fetchData]);

  useEffect(() => {
    console.log('Current rules state:', rules);
    console.log('Current contents state:', contents);
  }, [rules, contents]);

  const handleConditionChange = (index, event) => {
    const newConditions = [...conditions];
    newConditions[index][event.target.name] = event.target.value;
    setConditions(newConditions);
  };

  const addCondition = () => {
    setConditions([...conditions, { requiredPostId: '' }]);
  };

  const removeCondition = (index) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    setConditions(newConditions);
  };

  const resetForm = () => {
    setName('');
    // setDescription(''); // Удалено
    setConditions([{ requiredPostId: '' }]);
    setTargetPostId('');
    setNewPrice('');
    setCurrentRule(null);
    setIsEditing(false);
    setShowForm(false); // Раскомментировано/добавлено для закрытия формы
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const ruleData = { name, conditions, targetPostId, newPrice: Number(newPrice) }; // description удалено

    const url = isEditing ? `/api/marketing/${currentRule._id}` : '/api/marketing';
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(ruleData)
      });
      if (!response.ok) throw new Error('Ошибка сохранения правила');
      const savedRule = await response.json();
      if (isEditing) {
        setRules(rules.map(r => r._id === savedRule._id ? savedRule : r));
      } else {
        setRules([...rules, savedRule]);
      }
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (rule) => {
    setIsEditing(true);
    setCurrentRule(rule);
    setName(rule.name);
    // setDescription(rule.description); // Удалено
    setConditions(rule.conditions.map(c => ({ 
      requiredPostId: typeof c.requiredPostId === 'object' && c.requiredPostId !== null ? c.requiredPostId._id : c.requiredPostId 
    })));
    setTargetPostId(typeof rule.targetPostId === 'object' && rule.targetPostId !== null ? rule.targetPostId._id : rule.targetPostId);
    setNewPrice(rule.newPrice);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить это правило?')) {
      try {
        const response = await fetch(`/api/marketing/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Ошибка удаления правила');
        setRules(rules.filter(r => r._id !== id));
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleToggleStatus = async (id, isActive) => {
    try {
      const response = await fetch(`/api/marketing/${id}/toggle`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Ошибка изменения статуса');
      setRules(rules.map(r => r._id === id ? { ...r, isActive: !r.isActive } : r));
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="loading-state"><div className="loading-spinner"></div><p>Загрузка...</p></div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="marketing-management">
      {!showForm && (
        <div className="page-header"> {/* Оставляем page-header для кнопки "Новое условие", но убираем заголовок */}
          <button className="add-button" onClick={() => { setShowForm(true); setIsEditing(false); resetForm(); }}>
            <FaPlus /> Новое условие
          </button>
        </div>
      )}

      {showForm && (
        <div className="marketing-form">
          {/* Кнопка Назад к списку */}
          <div className="form-header-actions form-back-button-container"> {/* Инлайн-стиль заменен на класс */}
            <button type="button" className="admin-button icon-button" onClick={() => setShowForm(false)}> {/* Изменены классы */}
              <FaArrowLeft /> Назад к списку
            </button>
          </div>
          {/* Заголовок удален */}
          <form onSubmit={handleSubmit}>
            <input type="text" placeholder="Название" value={name} onChange={(e) => setName(e.target.value)} required disabled={isSubmitting} />
            {/* Поле описания удалено */}
            
            <h4>Условия (если пользователь купил...)</h4>
            {conditions.map((cond, index) => (
              <div key={index} className="condition-item">
                <select name="requiredPostId" value={cond.requiredPostId} onChange={(e) => handleConditionChange(index, e)} required disabled={isSubmitting}>
                  <option value="">Выберите пост</option>
                  {contents && contents.filter(c => !c.isFree && !c.isAnnouncement).map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                </select>
                {conditions.length > 1 && (
                  <button type="button" className="admin-button-delete" onClick={() => removeCondition(index)} disabled={isSubmitting}>
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="admin-button" onClick={addCondition} disabled={isSubmitting}>Добавить условие</button>

            <h4 style={{marginTop: '20px'}}>То цена на пост...</h4>
            <select value={targetPostId} onChange={(e) => setTargetPostId(e.target.value)} required disabled={isSubmitting}>
              <option value="">Выберите пост</option>
              {contents && contents.filter(c => !c.isFree && !c.isAnnouncement).map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
            </select>

            <h4 style={{marginTop: '20px'}}>...станет</h4>
            <input type="number" placeholder="Новая цена" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} required disabled={isSubmitting} />

            <div className="form-actions">
              <button type="submit" className="admin-button" disabled={isSubmitting}>{isSubmitting ? 'Сохранение...' : (isEditing ? 'Сохранить' : 'Создать')}</button>
              {/* Кнопка Отмена удалена снизу */}
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="content-list">
          {rules.map(rule => (
            <div key={rule._id} className="content-item">
              <div className="content-item-header">
                <div className="content-title">
                  <h3>{rule.name}</h3>
                  {/* Описание будет изменено ниже */}
                  <div className="content-meta">
                    {(() => {
                      const targetPostIdValue = typeof rule.targetPostId === 'object' && rule.targetPostId !== null ? rule.targetPostId._id : rule.targetPostId;
                      const targetContent = contents.find(c => c._id === targetPostIdValue);

                      const requiredContents = rule.conditions.map(cond => {
                        const reqPostIdValue = typeof cond.requiredPostId === 'object' && cond.requiredPostId !== null ? cond.requiredPostId._id : cond.requiredPostId;
                        return contents.find(c => c._id === reqPostIdValue);
                      }).filter(Boolean);
                      
                      if (!targetContent) {
                        // console.log("Целевой пост не найден для ID:", targetPostIdValue, "Полный rule.targetPostId:", rule.targetPostId);
                        return "Целевой пост не найден";
                      }

                      return (
                        <React.Fragment>
                          <div>Цена на "{targetContent.title}"</div>
                          <div>{rule.newPrice}₽</div>
                          {requiredContents.length > 0 && (
                            <div>Если купил: {requiredContents.map(rc => `"${rc.title}"`).join(', ')}</div>
                          )}
                        </React.Fragment>
                      );
                    })()}
                  </div>
                </div>
                <div className="content-status">
                  <span className={`status-icon ${rule.isActive ? 'published' : 'draft'}`} title={rule.isActive ? 'Активно' : 'Неактивно'}>
                    {rule.isActive ? <FaToggleOn /> : <FaToggleOff />}
                  </span>
                </div>
              </div>
              <div className="content-actions">
                <div className="button-row">
                  <button className="admin-button" onClick={() => handleToggleStatus(rule._id, rule.isActive)}>
                    {rule.isActive ? 'Деактивировать' : 'Активировать'}
                  </button>
                  <button className="admin-button" onClick={() => handleEdit(rule)}>
                    <FaEdit /> Изменить
                  </button>
                  <button className="admin-button-delete" onClick={() => handleDelete(rule._id)}>
                    <FaTrash /> Удалить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MarketingManagement;
